import { defineStore } from 'pinia';
import { Cookies, LocalStorage } from 'quasar';
import { jwtDecode } from 'jwt-decode';
import { Subscription } from 'src/js/models/subscription';
import { Capacitor } from '@capacitor/core';
import { useBrandConfig } from 'src/composables/useBrandConfig';

const brandConfig = useBrandConfig();
const PWD_MERGE_PROMPT_INTERVAL_MS = 12 * 60 * 60 * 1000;
const ACCOUNT_CACHE_CLEANUP_KEY = 'gapgpt:account_cache_cleanup_v1';
const NEW_CHAT_CONVERSION_BANNER_KEY = '__new_chat__';

function conversionBannerChatKey(chatToken?: string | null): string {
   const token = typeof chatToken === 'string' ? chatToken.trim() : '';
   return token && !['new', 'voice'].includes(token) ? token : NEW_CHAT_CONVERSION_BANNER_KEY;
}

function accountIdentityFromToken(token?: string | null): string | null {
   if (!token) return null;
   try {
      const userId = jwtDecode<{ user_id?: unknown }>(token)?.user_id?.toString().trim();
      return userId || null;
   } catch {}
   return null;
}

function clearAccountCaches() {
   try {
      ['cachedChatList', 'cachedProjectList', 'gapgpt:cachedProfile'].forEach(key => LocalStorage.removeItem(key));
      for (const key of LocalStorage.getAllKeys()) {
         if (typeof key === 'string' && (key.startsWith('chat:') || key.startsWith('gapgpt:account:'))) {
            LocalStorage.removeItem(key);
         }
      }
   } catch {}
}

function clearLegacyAccountCachesOnce() {
   if (process.env.MODE === 'ssg') return;
   try {
      if (LocalStorage.getItem(ACCOUNT_CACHE_CLEANUP_KEY)) return;
      clearAccountCaches();
      LocalStorage.setItem(ACCOUNT_CACHE_CLEANUP_KEY, Date.now());
   } catch {}
}

async function clearAccountStores(pinia?: any) {
   try {
      const chatStore = (await import('src/stores/chat')).useChatStore(pinia);
      chatStore.chats.splice(0);
      chatStore.nextChatListURL = '';
      chatStore.nextArchivedChatListURL = '';
      chatStore.allChatCount = 0;
      chatStore.allArchivedChatCount = 0;
      chatStore.chatListLoading = true;
      chatStore.activeChatToken = null;
   } catch {}

   try {
      const projectStore = (await import('src/stores/project')).useProjectStore(pinia);
      projectStore.projects.splice(0);
      projectStore.loading = false;
      projectStore.loaded = false;
   } catch {}
}

clearLegacyAccountCachesOnce();

export function jwtIsExpiring(jwtToken: string) {
   try {
      const decoded = jwtDecode<{ exp?: number; user_id?: unknown }>(jwtToken);
      const userId = decoded?.user_id;
      if (typeof userId === 'number') return true;
      if (typeof userId === 'string' && /^\d+$/.test(userId)) return true;

      const exp = decoded?.exp;
      if (typeof exp !== 'number') return true;

      return exp - 60 * 60 * 24 * 7 <= Math.trunc(Date.now() / 1000);
   } catch {
      return true;
   }
}

function getStorageItem(key: string) {
   if (process.env.MODE === 'ssg') return undefined;
   try {
      return Cookies.get(key) ?? localStorage.getItem(key);
   } catch (e) {
      console.error('Storage access error:', e);
      return undefined;
   }
}

function shouldShowPwdMergePrompt(data: any) {
   if (process.env.MODE === 'ssg' || data?.register_platform !== 'web_password') {
      return false;
   }

   try {
      const profileKey = data?.token || data?.phone_number || 'unknown';
      const storageKey = `gapgpt:pwdMergePrompt:lastShown:${profileKey}`;
      const lastShown = Number(LocalStorage.getItem(storageKey) || 0);
      const now = Date.now();
      if (lastShown && now - lastShown < PWD_MERGE_PROMPT_INTERVAL_MS) {
         return false;
      }
      LocalStorage.setItem(storageKey, now);
      return true;
   } catch (e) {
      console.error('PWD merge prompt storage failed:', e);
      return true;
   }
}

export const useAuthStore = defineStore('auth', {
   state: () => ({
      userToken: undefined,
      subscription: undefined as Subscription | undefined,
      lastSubscription: null,
      lastPaidSubscription: null as Subscription | null,
      hasDiscount: false,
      profile: undefined,
      closeLimitBanner: false,
      closeConversionBanner: false,
      closedConversionBannerChatTokens: [] as string[],
      showFileLimitBanner: false,
      showImageFileLimitBanner: false,
      advMessagesRemaining: null as number | null,
      accessToken: process.env.MODE === 'ssg' ? undefined : getStorageItem('accessToken'),
      refreshToken: process.env.MODE === 'ssg' ? undefined : getStorageItem('refreshToken'),
      experiments: process.env.MODE === 'ssg' ? {} : Cookies.get('experiments') ?? {},
      subscriptionHistory: [],
      subscriptionPaymentScope: null as any,
      billingWalletPayments: null as any,
      workspaceSubscriptions: [],
      subscriptionHistoryPagination: {
         page: 1,
         rowsPerPage: 5,
         rowsNumber: 0
      },
      loadingSubscriptionHistory: false,
      fetchProfilePromise: null,
      authStateVersion: 0,
      isMyket: Cookies.get('is_myket') === 'true',

      personality: 'default',
      nickname: '',
      job: '',
      interests: '',
      custom_instruction: '',
      
      showPhoneVerificationDialog: false,
      showPhoneVerificationPromptDialog: false,
   }),
   getters: {
      isFreeOrNoSub(): boolean {
         return (
            this.profile &&
            (!this.subscription || this.subscription.subtype === 'F')
         );
      },
      attemptConversion(): boolean {
         return !!(
            !this.subscription ||
            !this.subscription.isPaid ||
            (this.profile && this.profile.limits.message_limited) ||
            (this.profile && ['GO', 'L'].includes(this.profile.subscription.subtype))
         );
      },
      showManageWorkspace() {
         // Only show workspace management if user has an active workspace subscription
         return this.profile !== undefined && this.profile.is_workspace_admin && this.profile.has_active_workspace_subscription;
      },
      appSource() {
         if(Cookies.get('is_google_play') === 'true')
            return 'google_play'
         if(Cookies.get('is_bazaar') === 'true')
            return 'bazaar'
         if(Cookies.get('is_myket') === 'true')
            return 'myket'
         if(Cookies.get('is_xiaomi') === 'true')
            return 'xiaomi'
         if (Cookies.get('is_huawei') === 'true')
            return 'huawei'
         if (Cookies.get('is_samsung') === 'true')
            return 'samsung'

         return 'unknown'
      },
      showPaymentGateway() {
         if (!brandConfig.showPayment)
            return false
         if (Cookies.get('is_bazaar') === 'true' && Capacitor.Plugins.BazaarPayment) {
            return true
            // return false // bazaar review
            const installTime = Cookies.get('device_install_time')
            const installDate = installTime ? new Date(installTime) : null
            const cutoff = new Date('2025-09-29T00:00:00.000Z')
            if (installDate && !Number.isNaN(installDate.getTime()) && installDate > cutoff)
               return false
         }
         if (this.isGooglePlayTester)
            return false
         return true
      },
      isIranian() {
         return false
      },
      isGooglePlayTester() {
         return this.profile && this.profile.phone_number === '09998887766';
      },
   },
   actions: {
      async setTokens({ access, refresh }) {
         if (!access || !refresh) {
            await this.clearTokens();
            return;
         }

         const previousIdentity = accountIdentityFromToken(this.accessToken);
         const nextIdentity = accountIdentityFromToken(access);
         const identityChanged = previousIdentity !== nextIdentity;

         if (identityChanged) {
            this.fetchProfilePromise = null;
            this.authStateVersion += 1;
            this.closeLimitBanner = false;
            this.closeConversionBanner = false;
            this.closedConversionBannerChatTokens = [];
            clearAccountCaches();
            await clearAccountStores(this.$pinia);
         }

         this.accessToken = access;
         this.refreshToken = refresh;

         // Use secure cookie settings
         const cookieOptions = {
            expires: 365,
            path: '/',
            secure: window.location.protocol === 'https:', // Only send over HTTPS
            sameSite: 'Lax' as const
         };

         Cookies.set('accessToken', this.accessToken, cookieOptions);
         Cookies.set('refreshToken', this.refreshToken, cookieOptions);

         // Remove localStorage storage for security
         try {
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
         } catch (e) {
            console.error('LocalStorage cleanup failed:', e);
         }
      },
      async setProfileDetails(data) {
         this.userToken = data.token;
         this.hasDiscount = data.has_discount;
         this.profile = data;
         this.experiments = data.experiments;

         this.nickname = data.nickname || '';
         this.job = data.job || '';
         this.interests = data.interests || '';
         this.personality = data.personality || 'default';
         this.custom_instruction = data.custom_instruction || '';

         this.showPhoneVerificationDialog = false;
         if (shouldShowPwdMergePrompt(data)) {
            this.showPhoneVerificationPromptDialog = true;
         } else if (data?.register_platform !== 'web_password') {
            this.showPhoneVerificationPromptDialog = false;
         }

         // Capture the current tier before overwriting it, so we can detect an
         // upgrade/downgrade below and re-sync the default chat model.
         const previousSubtype = this.subscription?.subtype;
         // Restore subscription objects from profile payload
         if (data.subscription) {
            this.subscription = new Subscription(data.subscription);
         } else {
            this.subscription = undefined;
         }
         if (data.last_subscription) {
            this.lastSubscription = new Subscription(data.last_subscription);
         } else {
            this.lastSubscription = null;
         }
         if ((data as any).last_paid_subscription) {
            this.lastPaidSubscription = new Subscription((data as any).last_paid_subscription);
         } else {
            this.lastPaidSubscription = null;
         }
         try {
            // sync chat store memory toggle from profile when present
            const { useChatStore } = await import('src/stores/chat');
            const chat = useChatStore(this.$pinia);
            const flag = (data as any)?.memory_enabled;
            const subtype = data.subscription?.subtype;

            // Memory remains unavailable to free users; paid users keep their stored preference.
            if (typeof flag === 'boolean') {
               chat.enableUserMemory = typeof subtype === 'string' && subtype === 'F' ? false : flag;
            }

            // When the subscription tier changes (e.g. a free user upgrades), the
            // default model must follow — free→A-FREEGPT, paid→A-GAP. Doing it here
            // means it happens on every profile update (payment recheck, socket
            // update_profile, init), even when no model selector is mounted.
            if (subtype && subtype !== previousSubtype) {
               chat.syncPrimaryModelForSubscription(subtype);
            }
         } catch {}
         try {
            if (process.env.MODE !== 'ssg') {
               const dataCopy = { ...data }
               dataCopy.limits = undefined
               const cached = {
                  data: dataCopy,
                  cached_at: Date.now(),
               };
               LocalStorage.setItem('gapgpt:cachedProfile', cached);
            }
         } catch (e) {
            console.error('Failed to cache profile:', e);
         }
      },
      getexp(exp_name) {
         return this.experiments && exp_name in this.experiments ? this.experiments[exp_name] : undefined;
      },
      isConversionBannerClosedForChat(chatToken?: string | null) {
         return this.closedConversionBannerChatTokens.includes(conversionBannerChatKey(chatToken));
      },
      closeConversionBannerForChat(chatToken?: string | null) {
         const key = conversionBannerChatKey(chatToken);
         if (!this.closedConversionBannerChatTokens.includes(key)) {
            this.closedConversionBannerChatTokens.push(key);
         }
         this.closeConversionBanner = true;
      },
      inheritNewChatConversionBannerClose(chatToken?: string | null) {
         const key = conversionBannerChatKey(chatToken);
         if (key !== NEW_CHAT_CONVERSION_BANNER_KEY && this.isConversionBannerClosedForChat(null)) {
            this.closeConversionBannerForChat(chatToken);
            this.closedConversionBannerChatTokens = this.closedConversionBannerChatTokens.filter(
               token => token !== NEW_CHAT_CONVERSION_BANNER_KEY
            );
         }
      },
      async updateProfileDetails() {
         // Prevent parallel requests and reuse in-flight promise
         if (this.fetchProfilePromise) return this.fetchProfilePromise;
         const requestAuthStateVersion = this.authStateVersion;
         const profilePromise = this.$api.get('profile')
            .then((response) => {
               if (this.authStateVersion !== requestAuthStateVersion || this.fetchProfilePromise !== profilePromise) {
                  return undefined;
               }
               const data = response.data;
               this.setProfileDetails(data);
               return data;
            })
            .finally(() => {
               if (this.fetchProfilePromise === profilePromise) {
                  this.fetchProfilePromise = null;
               }
            });
         this.fetchProfilePromise = profilePromise;
         return this.fetchProfilePromise;
      },
      async sendSubscriptionToFirebase() {
         // if (Platform.is.capacitor) {
         //    try {
         //       const response = await this.$api.post('sub_firebase');
         //       if (response.data['status'] === 'ok') {
         //          // import('@capacitor-firebase/analytics').then(({ FirebaseAnalytics }) => {
         //          //    FirebaseAnalytics.logEvent({
         //          //       name: 'app_subscribe',
         //          //       params: {
         //          //          subscriptionAmount: response.data['dollarPrice'],
         //          //          subscriptionId: response.data['subToken'],
         //          //          currency: 'USD',
         //          //       },
         //          //    });
         //          // })
         //       }
         //    } catch (error) {
         //       console.error('Error sending subscription to Firebase:', error);
         //    }
         // }
      },
      bindProfileDetails() {
         this.$socket.alwaysListenEvent('update_profile', async (all_data) => {
            const data = all_data.profile;
            await this.setProfileDetails(data);
         });
      },
      isAuthenticated() {
         return !!this.accessToken || !!this.refreshToken
      },
      setExperiment(key, val) {
         if (!this.experiments) {
            this.experiments = {};
         }
         this.experiments[key] = val;
         Cookies.set('experiments', this.experiments, {
            expires: 365,
            path: '/',
         });
      },
      async logoutFromAllDevices() {
         try {
            // Get phone number from profile if available
            const phoneNumber = this.profile?.phone_number;

            // Make API call with phone number in the request body
            const response = await this.$api.post('logout_all_sessions', {
               phone_number: phoneNumber
            });

            if (response.status === 200) {
               // Clear local tokens using secure method
               await this.clearTokens();
               return { success: true };
            } else {
               throw new Error('Failed to logout from all sessions');
            }
         } catch (error) {
            console.error('Error logging out from all sessions:', error);
            return {
               success: false,
               error: error instanceof Error ? error.message : 'Unknown error'
            };
         }
      },
      async clearTokens() {
         this.accessToken = undefined;
         this.refreshToken = undefined;
         this.userToken = undefined;
         this.profile = undefined;
         this.subscription = undefined;
         this.lastSubscription = null;
         this.lastPaidSubscription = null;
         this.fetchProfilePromise = null;
         this.authStateVersion += 1;
         this.closeLimitBanner = false;
         this.closeConversionBanner = false;
         this.closedConversionBannerChatTokens = [];

         try {
            Cookies.remove('accessToken', { path: '/' });
            Cookies.remove('refreshToken', { path: '/' });
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            clearAccountCaches();
            await clearAccountStores(this.$pinia);
         } catch (e) {
            console.error('Token cleanup failed:', e);
         }
      },
      async logout() {
         // Unregister device for push notifications if device_id is available
         const deviceId = Cookies.get('device_id');
         if (deviceId && this.isAuthenticated()) {
            try {
               await this.$api.post('/unregister_device', { device_id: deviceId });
            } catch {
               // Silently fail - don't block logout if unregister fails
            }
         }
         
         // Clear tokens and local storage
         await this.setTokens({ access: '', refresh: '' });
         try {
            LocalStorage.clear();
            this.advMessagesRemaining = null;
         } catch (e) {
            console.error('LocalStorage cleanup failed:', e);
         }
      },
      async deleteAllChats() {
         try {
            const response = await this.$api.post('delete_all_chats');

            if (response.status === 200) {
               return {
                  success: true,
                  deleted_count: response.data.deleted_count
               };
            } else {
               throw new Error('Failed to delete all chats');
            }
         } catch (error) {
            console.error('Error deleting all chats:', error);
            return {
               success: false,
               error: error instanceof Error ? error.message : 'Unknown error'
            };
         }
      },
      async clearUserMemory() {
         try {
            const response = await this.$api.post('clear_user_memory');

            if (response.status === 200) {
               return {
                  success: true,
                  message: response.data.message || 'Memory cleared successfully'
               };
            } else {
               throw new Error('Failed to clear user memory');
            }
         } catch (error) {
            console.error('Error clearing user memory:', error);
            return {
               success: false,
               error: error instanceof Error ? error.message : 'Unknown error'
            };
         }
      },
      async fetchSubscriptionHistory(page = 1, perPage = 5, supportActions = false) {
         this.loadingSubscriptionHistory = true;
         try {
            const response = await this.$api.get('subscriptions_list', {
               params: { page, per_page: perPage, support_actions: supportActions }
            });
            this.subscriptionHistory = response.data.subscriptions || [];
            this.subscriptionPaymentScope = response.data.payment_scope || null;
            this.billingWalletPayments = response.data.wallet_payments || null;
            this.subscriptionHistoryPagination = {
               ...this.subscriptionHistoryPagination,
               page,
               rowsPerPage: perPage,
               rowsNumber: response.data.total || 0
            };
            return {
               subscriptions: this.subscriptionHistory,
               pagination: this.subscriptionHistoryPagination
            };
         } catch (error) {
            console.error('Error fetching subscriptions:', error);
            return { error };
         } finally {
            this.loadingSubscriptionHistory = false;
         }
      },
      async fetchAllSubscriptionHistory(perPage = 50, supportActions = false) {
         this.loadingSubscriptionHistory = true;
         try {
            const allSubscriptions: any[] = [];
            let page = 1;
            let total = 0;
            let paymentScope: any = null;

            while (page <= 100) {
               const response = await this.$api.get('subscriptions_list', {
                  params: { page, per_page: perPage, support_actions: supportActions }
               });
               const subscriptions = response.data.subscriptions || [];
               total = Number(response.data.total || 0);
               if (response.data.payment_scope) {
                  paymentScope = response.data.payment_scope;
               }
               if (response.data.wallet_payments) {
                  this.billingWalletPayments = response.data.wallet_payments;
               }
               allSubscriptions.push(...subscriptions);

               if (subscriptions.length === 0 || allSubscriptions.length >= total) break;
               page += 1;
            }

            this.subscriptionHistory = allSubscriptions;
            this.subscriptionPaymentScope = paymentScope;
            if (!paymentScope) {
               this.billingWalletPayments = null;
            }
            this.subscriptionHistoryPagination = {
               ...this.subscriptionHistoryPagination,
               page: 1,
               rowsPerPage: perPage,
               rowsNumber: total || allSubscriptions.length
            };
            return {
               subscriptions: this.subscriptionHistory,
               pagination: this.subscriptionHistoryPagination,
               total: total || allSubscriptions.length
            };
         } catch (error) {
            console.error('Error fetching all subscriptions:', error);
            return { error };
         } finally {
            this.loadingSubscriptionHistory = false;
         }
      },
      async fetchWorkspaceSubscriptions() {
         try {
            const response = await this.$api.get('subscriptions_list', {
               params: { fetch_workspace_subscriptions: true }
            });
            this.workspaceSubscriptions = response.data.subscriptions || [];
            return {
               subscriptions: this.workspaceSubscriptions
            };
         } catch (error) {
            console.error('Error fetching workspace subscriptions:', error);
            return { error };
         }
      },
      async fetchInvoiceInfo() {
         try {
            const response = await this.$api.get('invoice_info');
            return response.data;
         } catch (error) {
            console.error('Error fetching invoice info:', error);
            throw error;
         }
      },
      async saveInvoiceInfo(payload) {
         try {
            const response = await this.$api.post('invoice_info', payload);
            return response.data;
         } catch (error) {
            console.error('Error saving invoice info:', error);
            throw error;
         }
      },
      async issueInvoiceWithPayload(payload) {
         try {
            // Make a POST request with the complete invoice payload
            const response = await this.$api.post('invoice_info', payload);
            return response.data;
         } catch (error) {
            console.error('Error issuing invoice with payload:', error);
            throw error;
         }
      }
   },
});
